/**
 * Tab Color System
 * Provides color schemes for tabs at different hierarchy levels
 * Level 1: Primary tabs (main navigation)
 * Level 2: Sub-tabs (within a primary tab)
 * Level 3: Sub-sub-tabs (within a sub-tab)
 */

export const tabColors = {
  level1: {
    active: {
      background: '#dbeafe', // Blue-100 background
      color: '#1e40af', // Blue-800 for better contrast
      borderBottom: '#1e40af',
      fontWeight: 600
    },
    inactive: {
      background: '#f3f4f6', // Gray-100 background
      color: '#4b5563', // Gray-600
      borderBottom: 'transparent',
      fontWeight: 400
    },
    hover: {
      background: '#e0e7ff', // Blue-200 on hover
      color: '#1e40af',
      borderBottom: '#1e40af'
    }
  },
  level2: {
    active: {
      background: '#d1fae5', // Emerald-100 background
      color: '#059669', // Emerald-600 for level 2
      borderBottom: '#059669',
      fontWeight: 600
    },
    inactive: {
      background: '#f3f4f6', // Gray-100 background
      color: '#6b7280', // Gray-500
      borderBottom: 'transparent',
      fontWeight: 400
    },
    hover: {
      background: '#a7f3d0', // Emerald-200 on hover
      color: '#059669',
      borderBottom: '#059669'
    }
  },
  level3: {
    active: {
      background: '#fee2e2', // Red-100 background
      color: '#dc2626', // Red-600 for level 3
      borderBottom: '#dc2626',
      fontWeight: 600
    },
    inactive: {
      background: '#f3f4f6', // Gray-100 background
      color: '#9ca3af', // Gray-400
      borderBottom: 'transparent',
      fontWeight: 400
    },
    hover: {
      background: '#fecaca', // Red-200 on hover
      color: '#dc2626',
      borderBottom: '#dc2626'
    }
  }
};

/**
 * Get tab styles for a given level and active state
 * @param {number} level - Tab level (1, 2, or 3)
 * @param {boolean} isActive - Whether the tab is currently active
 * @returns {object} Style object for the tab
 */
export function getTabStyles(level, isActive) {
  const levelKey = `level${level}`;
  const stateKey = isActive ? 'active' : 'inactive';
  const colors = tabColors[levelKey] || tabColors.level1;
  
  return {
    background: colors[stateKey].background,
    color: colors[stateKey].color,
    border: 'none',
    borderBottom: `${isActive ? '2px' : '2px'} solid ${colors[stateKey].borderBottom}`,
    padding: '8px 16px',
    cursor: 'pointer',
    fontWeight: colors[stateKey].fontWeight,
    fontSize: '14px',
    transition: 'all 0.2s ease',
    borderRadius: '8px 8px 0 0' // Rounded top corners
  };
}

/**
 * Get hover styles for a tab at a given level
 * @param {number} level - Tab level (1, 2, or 3)
 * @returns {object} Hover style object
 */
export function getTabHoverStyles(level) {
  const levelKey = `level${level}`;
  const colors = tabColors[levelKey] || tabColors.level1;
  
  return {
    background: colors.hover.background,
    color: colors.hover.color,
    borderBottom: `2px solid ${colors.hover.borderBottom}`
  };
}

/**
 * Helper function to restore tab styles on mouse leave
 * @param {HTMLElement} element - The tab element
 * @param {number} level - Tab level (1, 2, or 3)
 * @param {boolean} isActive - Whether the tab is currently active
 */
export function restoreTabStyles(element, level, isActive) {
  const styles = getTabStyles(level, isActive);
  element.style.background = styles.background;
  element.style.color = styles.color;
  element.style.borderBottom = styles.borderBottom;
  element.style.fontWeight = styles.fontWeight;
}

